import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-ask-box>` says about itself.
 *
 * Ask is the capability that does not answer the question. It translates the question into a query,
 * the kernel runs the query under the asker's own access, and the rows are the answer — so this
 * piece's whole job is to show the query ABOVE the rows and let somebody change it. A placement that
 * hid the query would turn a checkable translation into an assertion nobody can audit, which is the
 * one thing the capability was designed not to be.
 *
 * Two decisions worth knowing before placing it.
 *
 * Editing the query does NOT ask the model again. `caos-ask-run` carries the edited spec and the
 * application runs that; the model already did the only thing it was asked to do. Re-translating
 * would throw away the correction the person just made.
 *
 * The query stays on screen when the result is empty. Zero rows is an answer ABOUT the query, and it
 * is the case where seeing the query matters most.
 */
const docs: ComponentDocs = {
  aliases: ['ask box', 'natural language query', 'ask a question', 'query spec', 'ask capability', 'natural language search'],

  answers: [
    'ask a question in words and see the query it became',
    'correct the query the question produced and run it again',
    'see which records answered a question, under my own access',
    'keep a question as a list view',
  ],

  placements: ['appPage', 'homePage', 'recordPage'],

  whenToUse:
    'Where somebody asks a question of the organisation’s own records and needs to trust the answer — ' +
    'because the query that produced it is on screen and editable.',

  insteadUse: [
    {
      name: 'QueryEditor',
      when:
        'the person writes the query themselves. That is a developer surface with tabs and a language; ' +
        'this one starts from a sentence and shows the query as a form.',
    },
    {
      name: 'ResultGrid',
      when: 'there is a result set to show and no question behind it — this piece composes that one.',
    },
    {
      name: 'MessageComposer',
      when:
        'the question is one turn of a conversation rather than a query with a result set. The ' +
        'assistant capability composes this one inside that surface.',
    },
  ],

  inputs: [
    {
      name: 'spec',
      type: 'json',
      readsBack: 'the spec as EDITED on screen, which is what Run would send — not the one that was set',
      description:
        'The query the question became: `{ object, fields, where?, limit? }`, exactly the shape the ' +
        'shipped `ai_ask` prompt declares as its output. A property, because it is structured data. ' +
        'Setting it replaces what is on screen and resets “edited”.',
    },
    {
      name: 'columns',
      type: 'json',
      description: 'The columns of the result list, in the shape `<caos-result-grid>` takes, so nothing is transliterated.',
      default: [],
    },
    {
      name: 'rows',
      type: 'json',
      description:
        'The rows the query returned. Setting it — even to an empty list — is what tells this piece a ' +
        'run has happened, so “no records match” can read differently from “nothing has been asked yet”.',
      default: [],
    },
    {
      name: 'utterance',
      attribute: 'utterance',
      type: 'text',
      description:
        'The question as it was asked, shown above the query. Absent ⇒ the line is not drawn, for a ' +
        'placement that already shows the question somewhere else.',
    },
    {
      name: 'running',
      attribute: 'running',
      type: 'boolean',
      description: 'The application is running the query. Run reads “Running…” and the inputs are held.',
      default: false,
    },
    {
      name: 'readonly',
      attribute: 'readonly',
      type: 'boolean',
      description:
        'Show the query without letting anybody change it — for a saved answer being read back, where ' +
        'the query is evidence rather than a control.',
      default: false,
    },
    {
      name: 'runLabel',
      attribute: 'run-label',
      type: 'text',
      description: 'What the run control says.',
      default: 'Run',
    },
    {
      name: 'saveLabel',
      attribute: 'save-label',
      type: 'text',
      description: 'What the keep-this-question control says.',
      default: 'Save as list view',
    },
    {
      name: 'emptyText',
      attribute: 'empty-text',
      type: 'text',
      description: 'What to say when the query ran and matched nothing.',
      default: 'No records match this query.',
    },
  ],

  events: [
    {
      name: 'caos-ask-run',
      when: 'the run control is pressed',
      carries: '`{ spec, edited }` — the spec as it is on screen, and whether it differs from the one that arrived',
    },
    {
      name: 'caos-ask-spec-change',
      when: 'any part of the query is typed in',
      carries: '`{ spec, edited }`',
    },
    {
      name: 'caos-ask-save-view',
      when: 'the keep-this-question control is pressed',
      carries: '`{ spec, edited }`',
    },
  ],

  state: [
    {
      name: 'spec',
      type: 'CaosAskSpec | null',
      description:
        'The query on screen, which is the edited one. Read it to run the query without waiting for ' +
        'the event, or to store what somebody settled on.',
    },
    {
      name: 'edited',
      type: 'boolean',
      description:
        'Whether what is on screen still matches the query the question produced. Read it to record ' +
        'that a human corrected the translation — which is the signal an eval suite wants.',
    },
  ],

  parts: [
    { part: 'ask', is: 'The outer column.' },
    { part: 'utterance', is: 'The question as it was asked.', when: '`utterance` is set' },
    { part: 'spec', is: 'The query panel.' },
    { part: 'spec-row', is: 'One line of the query — object, fields, where, limit.' },
    { part: 'spec-label', is: 'The label beside one line.' },
    { part: 'spec-input', is: 'The editable box on one line.' },
    { part: 'spec-actions', is: 'The row holding the edited notice and the two controls.' },
    { part: 'run', is: 'The run control.' },
    { part: 'save', is: 'The keep-this-question control.' },
    { part: 'results', is: 'The composed result grid.', when: 'the query returned at least one row' },
    { part: 'empty', is: 'The “no records match” line.', when: 'the query ran and returned nothing' },
  ],

  accessibility:
    'The query is a `role="group"` labelled as the query the question became, so the relationship ' +
    'between the sentence above it and the form is spoken rather than only laid out. Each line’s label ' +
    'is a real `<label>` bound to its box. The “no records match” line is a `role="status"` live ' +
    'region, so a run that returns nothing is announced instead of leaving a reader waiting. That the ' +
    'query has been edited is stated in words beside the run control, not conveyed by colour alone.',

  examples: [
    {
      key: 'translated',
      label: 'A question, and the query it became',
      summary:
        'The default shape: the sentence, the query under it, the rows under that. The query is a form ' +
        'rather than a sentence, so a reader can see that `where` is what narrowed the answer.',
      placement: {
        component: 'caos-ask-box',
        inputs: {
          utterance: 'which accounts have no owner?',
          spec: { object: 'account', fields: ['name', 'owner', 'created_at'], where: "owner = null", limit: 50 },
          columns: [
            { key: 'name', label: 'Name' },
            { key: 'owner', label: 'Owner' },
            { key: 'created_at', label: 'Created' },
          ],
          rows: [
            { name: 'Northwind Trading', owner: null, created_at: '2026-02-11' },
            { name: 'Harbour Freight Lines', owner: null, created_at: '2026-05-03' },
          ],
        },
      },
      stage: { needsWidth: 560 },
    },
    {
      key: 'empty-answer',
      label: 'A query that matched nothing',
      summary:
        'Zero rows with the query still on screen. “No records match this query” plus the predicate is ' +
        'information; “nothing found” on its own is not, and hiding the query here would withhold the ' +
        'explanation exactly where it was needed.',
      placement: {
        component: 'caos-ask-box',
        inputs: {
          utterance: 'which contacts opted out last week?',
          spec: { object: 'contact', fields: ['name', 'email'], where: "opted_out_at > '2026-10-17'" },
          columns: [
            { key: 'name', label: 'Name' },
            { key: 'email', label: 'Email' },
          ],
          rows: [],
        },
      },
      stage: { needsWidth: 560 },
    },
    {
      key: 'read-back',
      label: 'A saved answer, read back',
      summary:
        'With `readonly`, the query is evidence rather than a control: this is the question that was ' +
        'asked and the query it ran. Nothing can be changed, and the query is still legible.',
      placement: {
        component: 'caos-ask-box',
        inputs: {
          readonly: true,
          utterance: 'open cases older than thirty days',
          spec: { object: 'case', fields: ['subject', 'status', 'opened_at'], where: "status = 'open'", limit: 200 },
          columns: [
            { key: 'subject', label: 'Subject' },
            { key: 'status', label: 'Status' },
          ],
          rows: [{ subject: 'Shipment arrived damaged', status: 'open' }],
        },
      },
      stage: { needsWidth: 560 },
    },
  ],
};

export default docs;
